import { Controller } from "react-hook-form";
import type { Control, FieldErrors, FieldValues } from "react-hook-form";
import { Input, Button } from "@legal-500/legal500-design-system";
import formStyles from "../../../components/Form/forms/GenericForm.module.css";
import styles from "./CreateCommentary.module.css";
import React from "react";

type ArrayFieldProps = {
  name: string;
  label: React.ReactNode;
  labelText?: string;
  max: number;
  control: Control<FieldValues>;
  errors: FieldErrors<FieldValues>;
  disabled?: boolean;
};

export function ArrayField({
  name,
  label,
  labelText,
  max,
  control,
  errors,
  disabled,
}: ArrayFieldProps) {
  const error = errors[name]?.message;
  const errorMessage = typeof error === "string" ? error : undefined;
  const errorId = `${name}-error`;

  return (
    <Controller
      name={name}
      control={control}
      render={({ field: controllerField }) => {
        const values: string[] = (controllerField.value as string[]) || [];

        const handleChange = (index: number, newValue: string) => {
          const updated = [...values];
          updated[index] = newValue;
          controllerField.onChange(updated);
        };

        const handleAdd = () => {
          controllerField.onChange([...values, ""]);
        };

        const handleRemove = (index: number) => {
          const updated = values.filter((_, i) => i !== index);
          controllerField.onChange(updated);
        };

        return (
          <div className={formStyles.formField}>
            <label className={formStyles.label}>
              {label} <span style={{ fontWeight: 400 }}>(max {max})</span>
            </label>
            <div className={styles.arrayFieldItems}>
              {values.map((value, index) => (
                <div key={`${name}-${index}`} className={styles.arrayFieldItem}>
                  <Input
                    value={value}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      handleChange(index, e.target.value)
                    }
                    placeholder={`${labelText ?? label} ${index + 1}`}
                    aria-label={`${labelText ?? label} ${index + 1}`}
                    disabled={disabled}
                  />
                  <Button
                    type="button"
                    variant="outlined"
                    size="small"
                    onClick={() => handleRemove(index)}
                    aria-label={`Remove ${labelText ?? label} ${index + 1}`}
                    disabled={disabled}
                  >
                    Remove
                  </Button>
                </div>
              ))}
            </div>
            {(values.length < max || disabled) && (
              <div className={styles.addButton}>
                <Button
                  type="button"
                  variant="outlined"
                  size="small"
                  onClick={handleAdd}
                  aria-label={`Add ${labelText ?? label}`}
                  disabled={disabled || values.length >= max}
                >
                  + Add
                </Button>
              </div>
            )}
            {errorMessage && (
              <span id={errorId} className={formStyles.errorText} role="alert" aria-live="polite">
                {errorMessage}
              </span>
            )}
          </div>
        );
      }}
    />
  );
}
